import { Head, Link } from "@inertiajs/react"
import {
  AppWindow,
  ChevronRight,
  Database,
  FolderArchive,
  Plus,
} from "lucide-react"

import Heading from "@/components/heading"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import {
  appPath,
  newServerAppPath,
  serverAppsPath,
  serverPath,
  serversPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"
import type { App, BackupConfig, Server } from "@/types"

interface AppsIndexProps {
  server: Pick<Server, "id" | "name">
  apps: (App & {
    backup_configs: Pick<BackupConfig, "id" | "backup_type" | "enabled">[]
  })[]
}

export default function AppsIndex({ server, apps }: AppsIndexProps) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Servers", href: serversPath() },
    { title: server.name, href: serverPath(server.id) },
    { title: "Apps", href: serverAppsPath(server.id) },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Apps - ${server.name}`} />

      <div className="p-6">
        <div className="flex items-center justify-between">
          <Heading
            title={`Apps on ${server.name}`}
            description="Manage applications deployed on this server"
          />
          <Button asChild>
            <Link href={newServerAppPath(server.id)}>
              <Plus className="mr-2 h-4 w-4" />
              Add App
            </Link>
          </Button>
        </div>

        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {apps.length === 0 ? (
            <Card className="col-span-full">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <AppWindow className="text-muted-foreground h-12 w-12" />
                <h3 className="mt-4 text-lg font-semibold">No apps yet</h3>
                <p className="text-muted-foreground mt-2 text-sm">
                  Add your first application to this server
                </p>
                <Button asChild className="mt-4">
                  <Link href={newServerAppPath(server.id)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add App
                  </Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            apps.map((app) => (
              <Link key={app.id} href={appPath(app.id)}>
                <Card className="group h-full cursor-pointer transition-shadow hover:shadow-md">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2">
                        <AppWindow className="h-5 w-5" />
                        {app.name}
                      </CardTitle>
                      <ChevronRight className="text-muted-foreground h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={
                          app.app_type === "rails" ? "default" : "secondary"
                        }
                        className="text-xs"
                      >
                        {app.app_type === "rails" ? "Rails" : "Static"}
                      </Badge>
                      {app.location && (
                        <CardDescription className="truncate">
                          {app.location}
                        </CardDescription>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {app.backup_configs.map((config) => (
                        <Badge
                          key={config.id}
                          variant={config.enabled ? "default" : "secondary"}
                          className="text-xs"
                        >
                          {config.backup_type === "database" ? (
                            <Database className="mr-1 h-3 w-3" />
                          ) : (
                            <FolderArchive className="mr-1 h-3 w-3" />
                          )}
                          {config.backup_type}
                        </Badge>
                      ))}
                      {app.backup_configs.length === 0 && (
                        <span className="text-muted-foreground text-xs">
                          No backups configured
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))
          )}
        </div>
      </div>
    </AppLayout>
  )
}
